import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Link, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCar, faPhone, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { useGetCarsByDealerQuery } from '../../services/carService/__generated__/queries.generated';
import { MoonLoader } from 'react-spinners';
import { URLS } from '../../../constants/urls';
import { useSnapshot } from 'valtio';
import { dealerStore } from '../../../store/dealerStore';
import CarCard from '../../components/car/CarCard';
import { useNavigate } from 'react-router-dom';
const PageContainer = styled.div`
  ${tw`w-full flex flex-col items-center bg-gray-900 text-white pb-8 md:pb-16 max-w-full min-w-0 box-border`}
`;

const HeroSection = styled.div`
  ${tw`
    w-full
    h-[500px]
    bg-cover
    bg-center
    flex
    items-center
    justify-center
    relative max-w-full min-w-0 box-border`}
  background-image: url('https://images.unsplash.com/photo-1552519507-da3b142c6e3d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80');

  &::before {
    content: '';
    ${tw`absolute inset-0 bg-black bg-opacity-50`}
  }
`;

const HeroContent = styled.div`
  ${tw`
    text-center
    z-10
    px-4
  `}
`;

const HeroTitle = styled.h1`
  ${tw`
    text-4xl
    md:text-5xl
    font-bold
    text-white
    mb-4
  `}
`;

const HeroSubtitle = styled.p`
  ${tw`
    text-xl
    md:text-2xl
    text-white
    mb-8
  `}
`;

const HeroButton = styled(Link)`
  ${tw`
    bg-red-500
    text-white
    py-3
    px-8
    rounded
    font-bold
    text-lg
    transition-colors
    duration-200
  `}
  &:hover {
    ${tw`bg-red-600`}
  }
`;

const FeaturedSection = styled.div`
  ${tw`
    py-16
    px-4
    max-w-screen-xl
    mx-auto
    w-full
    bg-gray-900
    box-border
  `}
`;

const SectionTitle = styled.h2`
  ${tw`
    text-3xl
    font-bold
    text-center
    mb-12
    text-white
  `}
`;

const CarGrid = styled.div`
  ${tw`
    grid
    grid-cols-1
    md:grid-cols-2
    lg:grid-cols-3
    gap-8
  `}
`;

const CarImage = styled.img`
  ${tw`
    w-full
    h-48
    object-cover
  `}
`;

const CarInfo = styled.div`
  ${tw`
    p-4
    bg-gray-800
  `}
`;

const CarName = styled.h3`
  ${tw`
    text-lg
    font-bold
    mb-2
    text-white
  `}
`;

const CarPrice = styled.p`
  ${tw`
    text-xl
    font-bold
    text-red-400
    mb-2
  `}
`;

const CarDetails = styled.p`
  ${tw`
    text-gray-300
    mb-4
  `}
`;

const ViewButton = styled(Link)`
  ${tw`
    inline-block
    bg-red-600
    text-white
    py-2
    px-4
    rounded
    font-medium
    transition-colors
    duration-200
  `}
  &:hover {
    ${tw`bg-red-700`}
  }
`;

const AboutSection = styled.div`
  ${tw`
    py-16
    px-4
    w-full
    bg-gray-800
    box-border
  `}
`;

const AboutContent = styled.div`
  ${tw`
    max-w-screen-xl
    mx-auto
    flex
    flex-col
    md:flex-row
    items-center
    gap-12
  `}
`;

const AboutImage = styled.img`
  ${tw`
    w-full
    md:w-1/2
    rounded-lg
    shadow-lg
  `}
`;

const AboutText = styled.div`
  ${tw`
    flex-1
    p-6
  `}
`;

const AboutTitle = styled.h2`
  ${tw`
    text-3xl
    font-bold
    mb-6
    text-white
  `}
`;

const AboutDescription = styled.p`
  ${tw`
    text-gray-300
    mb-6
    leading-relaxed
  `}
`;

const ContactInfo = styled.div`
  ${tw`
    flex
    flex-col
    gap-4
  `}
`;

const ContactItem = styled.div`
  ${tw`
    flex
    items-center
    gap-3
    text-gray-300
  `}
`;

const DealershipLanding = () => {
  const { dealerId } = useSnapshot(dealerStore);
  const navigate = useNavigate();
  const { data, error, loading } = useGetCarsByDealerQuery({
    variables: {
      input: {
        dealerId: dealerId ?? '',
      },
    },
  });

  const { getAllCarsOnSaleByDealer } = data ?? {};

  const renderFeaturedCars = () => {
    if (loading) {
      return <MoonLoader />;
    }
    if (error || !getAllCarsOnSaleByDealer || getAllCarsOnSaleByDealer.length === 0) {
      return null;
    }
    return (
      <FeaturedSection>
        <SectionTitle>Featured Vehicles</SectionTitle>
        <CarGrid>
          {getAllCarsOnSaleByDealer.map(car => {
            if (!car.name || !car.year || !car.make || !car.model || !car.thumbnailUrl) {
              return null;
            }
            return (
              <CarCard
                key={car.id}
                {...car}
                onDetailClicked={() => {
                  navigate(`/car-view/${car.id}`);
                }}
              />
            );
          })}
        </CarGrid>
      </FeaturedSection>
    );
  };

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>Find Your Perfect Car</HeroTitle>
          <HeroSubtitle>Quality pre-owned vehicles at competitive prices</HeroSubtitle>
          <HeroButton to="/cars_on_sale">Browse Inventory</HeroButton>
        </HeroContent>
      </HeroSection>

      {renderFeaturedCars()}

      <AboutSection>
        <AboutContent>
          <AboutImage
            src="https://images.unsplash.com/photo-1560958089-b8a1929cea89?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80"
            alt="Dealership"
          />
          <AboutText>
            <AboutTitle>About Our Dealership</AboutTitle>
            <AboutDescription>
              Welcome to Jidosoju Holding Inc. (DBA: Autopilotee Cars), your trusted source for
              quality pre-owned vehicles. With over number of years in the business, we pride
              ourselves on offering exceptional customer service and a wide selection of thoroughly
              inspected vehicles.
            </AboutDescription>
            <AboutDescription>
              Our team of experienced professionals is dedicated to helping you find the perfect car
              that fits your needs and budget. We offer competitive financing options and extended
              warranty plans to ensure your peace of mind.
            </AboutDescription>
            <ContactInfo>
              <ContactItem>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>2150 N first Street, San Jose, CA 95131</span>
              </ContactItem>
              <ContactItem>
                <FontAwesomeIcon icon={faPhone} />
                <span>+1(866)217-2849</span>
              </ContactItem>
            </ContactInfo>
          </AboutText>
        </AboutContent>
      </AboutSection>
    </PageContainer>
  );
};

export default DealershipLanding;
